import { MetroLine, Station, Timetable, TimetableEntry } from '../types';
import { calculateStationDistance } from './pathfinding';

// Function to calculate the direction based on station positions
function calculateDirection(
  currentStation: Station,
  nextStation: Station
): 'northbound' | 'southbound' | 'eastbound' | 'westbound' {
  const dx = nextStation.position.x - currentStation.position.x;
  const dy = nextStation.position.y - currentStation.position.y;
  
  // If horizontal movement is greater than vertical, it's east/west
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0 ? 'eastbound' : 'westbound';
  } else {
    return dy > 0 ? 'southbound' : 'northbound';
  }
}

// Generate a timetable for the entire metro system
export function generateTimetable(
  lines: Map<string, MetroLine>,
  stations: Map<string, Station>,
  startTime: number,
  durationMinutes: number = 60 // 1 hour by default
): Timetable {
  const timetable: Timetable = {
    entries: [],
    startTime,
    endTime: startTime + durationMinutes * 60 * 1000
  };
  
  lines.forEach((line, lineId) => {
    for (let trainIndex = 0; trainIndex < line.trainCount; trainIndex++) {
      // Calculate initial offset for this train
      const initialDelay = (trainIndex * line.frequency) % (line.frequency * line.trainCount);
      let currentTime = startTime + initialDelay;
      
      // Start at different positions along the line for better distribution
      const startStationIndex = Math.floor(trainIndex * line.stations.length / line.trainCount) % line.stations.length;
      const direction = trainIndex % 2 === 0 ? 1 : -1; // Alternate directions
      
      let currentStationIndex = startStationIndex;
      
      // Generate entries until we reach the end time
      while (currentTime < timetable.endTime) {
        const currentStationId = line.stations[currentStationIndex];
        const currentStation = stations.get(currentStationId);
        
        if (!currentStation) continue;
        
        // Calculate next station
        let nextStationIndex = currentStationIndex + direction;
        
        // Check if we need to change direction
        if (nextStationIndex < 0 || nextStationIndex >= line.stations.length) {
          // Reverse direction
          nextStationIndex = currentStationIndex - direction;
        }
        
        const nextStationId = line.stations[nextStationIndex];
        const nextStation = stations.get(nextStationId);
        
        if (!nextStation) continue;
        
        // Calculate travel time between stations
        const distance = calculateStationDistance(currentStation, nextStation);
        const travelTimeMs = (distance / 200) * 1000; // 200 pixels per second
        
        // Add random delay (0-20% of travel time)
        const randomDelay = Math.random() * travelTimeMs * 0.2;
        
        // Calculate arrival and departure times
        const arrivalTime = currentTime;
        const departureTime = currentTime + 10000 + randomDelay; // 10 seconds at station + random delay
        
        // Determine direction
        const directionLabel = calculateDirection(currentStation, nextStation);
        
        // Generate unique train ID
        const trainId = `${lineId}-train-${trainIndex + 1}`;
        
        // Create timetable entry
        const entry: TimetableEntry = {
          trainId,
          stationId: currentStationId,
          arrivalTime,
          departureTime,
          direction: directionLabel,
          platform: `${lineId.charAt(0).toUpperCase()}${trainIndex % 2 + 1}`
        };
        
        timetable.entries.push(entry);
        
        // Move to next station
        currentTime = departureTime + travelTimeMs;
        currentStationIndex = nextStationIndex;
      }
    }
  });
  
  // Sort entries by arrival time
  timetable.entries.sort((a, b) => a.arrivalTime - b.arrivalTime);
  
  return timetable;
}

// Get timetable entries for a specific station
export function getStationTimetable(
  timetable: Timetable,
  stationId: string,
  startTime: number = Date.now(),
  limitEntries: number = 10
): TimetableEntry[] {
  return timetable.entries
    .filter(entry => entry.stationId === stationId && entry.arrivalTime >= startTime)
    .slice(0, limitEntries);
}

// Get the next train arrival at a station
export function getNextTrainArrival(
  timetable: Timetable,
  stationId: string,
  lineId?: string,
  currentTime: number = Date.now()
): TimetableEntry | null {
  const entries = timetable.entries.filter(
    entry => 
      entry.stationId === stationId && 
      entry.arrivalTime > currentTime &&
      (!lineId || entry.trainId.startsWith(lineId))
  );
  
  if (entries.length === 0) {
    return null;
  }
  
  // Sort by arrival time and get the earliest
  entries.sort((a, b) => a.arrivalTime - b.arrivalTime);
  return entries[0];
}

// Format time for display
export function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Get time until next train (in seconds)
export function getTimeUntilNextTrain(
  timetable: Timetable,
  stationId: string,
  lineId?: string,
  currentTime: number = Date.now()
): number {
  const nextTrain = getNextTrainArrival(timetable, stationId, lineId, currentTime);
  
  if (!nextTrain) {
    return -1; // No trains scheduled
  }
  
  return Math.max(0, Math.floor((nextTrain.arrivalTime - currentTime) / 1000));
}

// Create a formatted timetable display for a station
export function formatStationTimetable(
  timetable: Timetable,
  stationId: string,
  currentTime: number = Date.now(),
  limitEntries: number = 5
): string {
  const entries = getStationTimetable(timetable, stationId, currentTime, limitEntries);
  
  if (entries.length === 0) {
    return "No scheduled trains";
  }
  
  let result = "Time | Train | Platform | Direction\n";
  result += "------|-------|----------|----------\n";
  
  entries.forEach(entry => {
    const time = formatTime(entry.arrivalTime);
    const train = entry.trainId.split('-')[0].toUpperCase();
    const platform = entry.platform || '-';
    const direction = entry.direction.charAt(0).toUpperCase() + entry.direction.slice(1);
    
    result += `${time} | ${train} | ${platform} | ${direction}\n`;
  });
  
  return result;
} 